import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import type { CompanyBrandingDTO, CompanyLogoVariant } from '@shared/schemas/company-branding';
import {
  getActiveCompanyBranding,
  getAdminCompanyBranding,
  removeActiveCompanyLogo,
  removeAdminCompanyLogo,
  uploadActiveCompanyLogo,
  uploadAdminCompanyLogo,
} from './company-branding-api';
import { companyBrandingQueryKeys } from './company-branding-query-keys';
import { CompanyUiError } from './company-errors';

export function brandingQueryKey(mode: 'active' | 'admin', companyId?: string | null) {
  return mode === 'admin' && companyId
    ? companyBrandingQueryKeys.admin(companyId)
    : companyBrandingQueryKeys.active;
}

export function writeCompanyBrandingCache(
  queryClient: QueryClient,
  branding: CompanyBrandingDTO,
  source: 'active' | 'admin',
) {
  queryClient.setQueryData(companyBrandingQueryKeys.admin(branding.companyId), branding);
  if (source === 'active') {
    queryClient.setQueryData(companyBrandingQueryKeys.active, branding);
    return;
  }
  queryClient.setQueryData(
    companyBrandingQueryKeys.active,
    (current: CompanyBrandingDTO | undefined) => {
      if (!current || current.companyId !== branding.companyId) return current;
      return branding;
    },
  );
}

export function useActiveCompanyBranding(enabled = true) {
  return useQuery({
    enabled,
    queryFn: getActiveCompanyBranding,
    queryKey: companyBrandingQueryKeys.active,
    retry: false,
    staleTime: 60_000,
  });
}

export function useCompanyBranding(mode: 'active' | 'admin', companyId?: string | null) {
  const enabled = mode === 'active' || Boolean(companyId);
  const queryKey = brandingQueryKey(mode, companyId);

  return useQuery({
    enabled,
    queryFn: () => (
      mode === 'admin' && companyId
        ? getAdminCompanyBranding(companyId)
        : getActiveCompanyBranding()
    ),
    queryKey,
    retry: false,
  });
}

type SaveCompanyLogoInput = {
  companyId?: string | null;
  file: File | null;
  mode: 'active' | 'admin';
  variant: CompanyLogoVariant;
};

export function useSaveCompanyLogo() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SaveCompanyLogoInput) => {
      if (input.mode === 'admin') {
        if (!input.companyId) {
          throw new CompanyUiError('Sélectionnez une entreprise.');
        }
        if (!input.file) {
          return removeAdminCompanyLogo(input.companyId, { variant: input.variant });
        }
        return uploadAdminCompanyLogo({
          companyId: input.companyId,
          file: input.file,
          variant: input.variant,
        });
      }

      if (!input.file) {
        return removeActiveCompanyLogo({ variant: input.variant });
      }
      return uploadActiveCompanyLogo({
        file: input.file,
        variant: input.variant,
      });
    },
    onSuccess: (branding, input) => {
      writeCompanyBrandingCache(queryClient, branding, input.mode);
    },
  });
}
